<template>
  <CollapsibleSection title="Informations personnelles" icon="fas fa-user" context="quote" storage-key="personal-info" :bare="bare">
    <DetailItem label="Civilité">
      <select
        v-if="isEditing"
        v-model="editedQuote.civilite"
        class="form-control"
        data-testid="quote-detail-civilite-select"
      >
        <option value="">--</option>
        <option value="mr">Mr</option>
        <option value="mme">Mme</option>
        <option value="autre">Autre</option>
      </select>
      <span v-else-if="quote.civilite" data-testid="quote-detail-civilite">{{ formatCivilite(quote.civilite) }}</span>
      <DetailEmpty v-else data-testid="quote-detail-civilite" />
    </DetailItem>
    <DetailItem label="Nom">
      <input
        v-if="isEditing"
        type="text"
        v-model="editedQuote.nom"
        class="form-control"
      />
      <span v-else-if="quote.nom">{{ quote.nom.toUpperCase() }}</span>
      <DetailEmpty v-else />
    </DetailItem>
    <DetailItem label="Prénom">
      <input
        v-if="isEditing"
        type="text"
        v-model="editedQuote.prenom"
        class="form-control"
      />
      <span v-else-if="quote.prenom">{{ quote.prenom }}</span>
      <DetailEmpty v-else />
    </DetailItem>
    <!-- `copy-value` : la ligne porte aussi la case « confirmée avec le client »
         du tunnel, dont le libellé se serait copié avec l'adresse. -->
    <DetailItem label="Email" :copy-value="quote.email" :copyable="!!quote.email">
      <input
        v-if="isEditing"
        type="email"
        v-model="editedQuote.email"
        class="form-control"
      />
      <span v-else-if="quote.email" data-testid="quote-detail-email">{{ quote.email }}</span>
      <DetailEmpty v-else data-testid="quote-detail-email" />
      <!-- Ce que l'analyse du besoin attache à l'adresse (la case « confirmée
           avec le client »), collé sous le champ qu'il confirme. La fiche de la
           demande ne le remplit pas. -->
      <slot name="besoin-email" />
    </DetailItem>
    <DetailItem label="Téléphone" :copy-value="quote.telephone">
      <input
        v-if="isEditing"
        type="tel"
        v-model="editedQuote.telephone"
        @input="handleTelephoneInput"
        class="form-control"
        placeholder="01 23 45 67 89"
      />
      <a v-else-if="quote.telephone" :href="`tel:${quote.telephone.replace(/\s/g, '')}`" class="phone-link">{{ formatPhone(quote.telephone) }}</a>
      <DetailEmpty v-else />
    </DetailItem>
    <DetailItem label="Type de client" :copyable="false">
      <RadioGroup
        v-if="isEditing"
        v-model="editedQuote.client_type"
        :options="clientTypeOptions"
      />
      <Badge v-else :variant="quote.client_type" size="sm">
        {{ quote.client_type === 'particulier' ? 'Particulier' : 'Professionnel' }}
      </Badge>
    </DetailItem>
    <DetailItem v-if="quote.source" label="Origine" :copy-value="quote.source">
      <span data-testid="quote-detail-source"><i class="fas fa-globe source-icon"></i> {{ quote.source }}</span>
    </DetailItem>
    <template #footer>
      <slot name="footer" />
    </template>
  </CollapsibleSection>
</template>

<script setup>
import { watch } from 'vue'
import { CollapsibleSection, DetailItem, DetailEmpty } from '../data'
import Badge from '../ui/Badge.vue'
import { RadioGroup } from '../ui'
import { useFormFormatting } from '../../composables/useFormFormatting.js'
import { formatPhone } from '../../utils/formatting.js'

const clientTypeOptions = [
  { value: 'particulier', label: 'Particulier' },
  { value: 'professionnel', label: 'Professionnel' },
]

const props = defineProps({
  /** Rend la section sans carte ni en-tête, pour l'imbriquer dans un bloc regroupé. */
  bare: {
    type: Boolean,
    default: false
  },
  quote: {
    type: Object,
    required: true
  },
  isEditing: {
    type: Boolean,
    default: false
  },
  editedQuote: {
    type: Object,
    default: () => ({})
  }
})

const { formatTelephone } = useFormFormatting()

// Formater le téléphone quand on entre en mode édition
watch(() => props.isEditing, (editing) => {
  if (editing && props.editedQuote.telephone) {
    props.editedQuote.telephone = formatTelephone(props.editedQuote.telephone)
  }
})

const formatCivilite = (civilite) => {
  const civiliteLabels = {
    mr: 'Mr',
    mme: 'Mme',
    autre: 'Autre'
  }
  // Appelé seulement quand `civilite` est renseignée (v-else-if côté template) :
  // l'absence se dit désormais par `DetailEmpty`, pas par ce « - ».
  return civiliteLabels[civilite] || civilite
}

const handleTelephoneInput = (event) => {
  const formatted = formatTelephone(event.target.value)
  props.editedQuote.telephone = formatted
}
</script>

<style scoped>
.phone-link {
  color: var(--color-primary);
  text-decoration: none;
  font-weight: 500;
}

.phone-link:hover {
  text-decoration: underline;
}

.form-control {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--color-border-dark);
  border-radius: var(--radius-sm);
  font-size: 1rem;
  font-family: inherit;
}

.form-control:focus {
  outline: 2px solid var(--color-info);
  outline-offset: 2px;
  border-color: var(--color-info);
  box-shadow: 0 0 0 3px var(--color-info-lighter);
}

.source-icon {
  opacity: 0.6;
  margin-right: var(--spacing-1);
}
</style>
